import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { inquiryService } from '../../../services/inquiryService';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';


const ContactForm = () => {
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    message: ''
  });

  // Add new state for file handling
  const [attachedFiles, setAttachedFiles] = useState([]);
  const [fileErrors, setFileErrors] = useState([]);
  const [uploading, setUploading] = useState(false);

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  // File validation constants
  const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
  const ALLOWED_FILE_TYPES = [
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'text/plain',
    'image/jpeg',
    'image/png',
    'image/webp'
  ];
  const MAX_FILES = 5;

  useEffect(() => {
    // Pre-fill form if user is logged in
    if (user && user?.email) {
      setFormData(prev => ({
        ...prev,
        email: user?.email,
        firstName: user?.user_metadata?.full_name?.split(' ')?.[0] || '',
        lastName: user?.user_metadata?.full_name?.split(' ')?.slice(1)?.join(' ') || ''
      }));
    }
  }, [user]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e?.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    // Clear error when user starts typing
    if (errors?.[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.firstName?.trim()) {
      newErrors.firstName = 'Vorname ist erforderlich';
    }

    if (!formData?.lastName?.trim()) {
      newErrors.lastName = 'Nachname ist erforderlich';
    }

    if (!formData?.email?.trim()) {
      newErrors.email = 'E-Mail-Adresse ist erforderlich';
    } else if (!/\S+@\S+\.\S+/?.test(formData?.email)) {
      newErrors.email = 'Ungültige E-Mail-Adresse';
    }

    if (!formData?.phone?.trim()) {
      newErrors.phone = 'Telefonnummer ist erforderlich';
    }

    if (!formData?.message?.trim()) {
      newErrors.message = 'Bitte beschreiben Sie Ihr Anliegen';
    } else if (formData?.message?.trim()?.length < 20) {
      newErrors.message = 'Bitte beschreiben Sie Ihr Anliegen ausführlicher (mindestens 20 Zeichen)';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  // Add file handling functions
  const validateFile = (file) => {
    const errors = [];

    if (file?.size > MAX_FILE_SIZE) {
      errors?.push('Datei ist zu groß (max. 10MB)');
    }

    if (!ALLOWED_FILE_TYPES?.includes(file?.type)) {
      errors?.push('Dateityp nicht unterstützt');
    }

    return errors;
  };

  const handleFileChange = (e) => {
    const files = Array.from(e?.target?.files);
    const newErrors = [];
    const validFiles = [];

    // Check total file count
    if (attachedFiles?.length + files?.length > MAX_FILES) {
      newErrors?.push(`Maximal ${MAX_FILES} Dateien erlaubt`);
      return;
    }

    files?.forEach((file, index) => {
      const fileValidationErrors = validateFile(file);
      if (fileValidationErrors?.length === 0) {
        validFiles?.push(file);
      } else {
        newErrors?.push(`${file?.name}: ${fileValidationErrors?.join(', ')}`);
      }
    });

    setAttachedFiles(prev => [...prev, ...validFiles]);
    setFileErrors(newErrors);

    // Clear file input
    e.target.value = '';
  };

  const removeFile = (index) => {
    setAttachedFiles(prev => prev?.filter((_, i) => i !== index));
    setFileErrors([]);
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i))?.toFixed(2)) + ' ' + sizes?.[i];
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setUploading(true);

    try {
      const { data, error } = await inquiryService?.createInquiry(
        {
          ...formData,
          userId: user?.id
        },
        attachedFiles
      );

      if (error) {
        setErrors({ submit: error });
        return;
      }

      // Success - reset form and show success message
      setFormData({
        firstName: user?.user_metadata?.full_name?.split(' ')?.[0] || '',
        lastName: user?.user_metadata?.full_name?.split(' ')?.slice(1)?.join(' ') || '',
        email: user?.email || '',
        phone: '',
        message: ''
      });
      
      setAttachedFiles([]);
      setFileErrors([]);
      setSubmitSuccess(true);
      setErrors({});
      
      // Hide success message after 5 seconds
      setTimeout(() => setSubmitSuccess(false), 5000);

    } catch (error) {
      setErrors({ submit: 'Es ist ein Fehler aufgetreten. Bitte versuchen Sie es erneut.' });
    } finally {
      setIsSubmitting(false);
      setUploading(false);
    }
  };

  if (submitSuccess) {
    return (
      <section className="py-16 lg:py-24 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-green-50 border border-green-200 rounded-2xl p-8 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h3 className="text-2xl font-bold text-green-600 mb-4">
              Anfrage erfolgreich übermittelt!
            </h3>
            <p className="text-green-700 mb-6">
              Vielen Dank für Ihre Anfrage! Wir haben Ihre Nachricht erhalten und melden uns binnen 24 Stunden bei Ihnen. 
              Eine Bestätigungs-E-Mail wurde an Ihre E-Mail-Adresse gesendet.
            </p>
            {user && (
              <Button
                onClick={() => window.location.href = '/dashboard'}
                variant="default"
                className="bg-green-600 hover:bg-green-700 text-white"
              >
                Zum Dashboard
              </Button>
            )}
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-16 lg:py-24 bg-white">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl lg:text-4xl font-bold text-text-primary mb-4">
            Schriftliche Rechtsberatung anfordern
          </h2>
          <p className="text-xl text-text-secondary max-w-2xl mx-auto">
            Schildern Sie uns Ihren Fall und erhalten Sie binnen 24 Stunden eine 
            professionelle rechtliche Beratung in schriftlicher Form.
          </p>
        </div>

        <div className="bg-surface rounded-2xl shadow-elevated p-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid md:grid-cols-2 gap-6">
              <Input
                label="Vorname"
                type="text"
                name="firstName"
                value={formData?.firstName}
                onChange={handleInputChange}
                error={errors?.firstName}
                required
                placeholder="Ihr Vorname"
              />

              <Input
                label="Nachname"
                type="text"
                name="lastName"
                value={formData?.lastName}
                onChange={handleInputChange}
                error={errors?.lastName}
                required
                placeholder="Ihr Nachname"
              />
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <Input
                label="E-Mail-Adresse"
                type="email"
                name="email"
                value={formData?.email}
                onChange={handleInputChange}
                error={errors?.email}
                required
                placeholder="ihre.email@beispiel.de"
              />

              <Input
                label="Telefonnummer"
                type="tel"
                name="phone"
                value={formData?.phone}
                onChange={handleInputChange}
                error={errors?.phone}
                required
                placeholder="+49 123 456 789"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-text-primary mb-2">
                Beschreibung Ihres Anliegens <span className="text-error">*</span>
              </label>
              <textarea
                name="message"
                value={formData?.message}
                onChange={handleInputChange}
                rows={6}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
                placeholder="Bitte beschreiben Sie Ihren Fall so detailliert wie möglich. Je mehr Informationen Sie uns geben, desto besser können wir Ihnen helfen."
                required
              />
              {errors?.message && (
                <p className="mt-1 text-sm text-error">{errors?.message}</p>
              )}
              <p className="mt-1 text-xs text-text-secondary">
                Mindestens 20 Zeichen erforderlich
              </p>
            </div>

            {/* Add file upload section */}
            <div>
              <label className="block text-sm font-medium text-text-primary mb-2">
                Anhänge (optional)
              </label>
              <div className="border-2 border-dashed border-slate-300 rounded-lg p-6 text-center hover:border-primary transition-colors">
                <input
                  type="file"
                  multiple
                  accept=".pdf,.doc,.docx,.txt,.jpg,.jpeg,.png,.webp"
                  onChange={handleFileChange}
                  className="hidden"
                  id="file-upload"
                  disabled={attachedFiles?.length >= MAX_FILES}
                />
                <label
                  htmlFor="file-upload"
                  className={`cursor-pointer flex flex-col items-center ${
                    attachedFiles?.length >= MAX_FILES ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                >
                  <svg
                    className="w-8 h-8 text-slate-400 mb-2"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                    />
                  </svg>
                  <span className="text-sm text-slate-600 mb-1">
                    {attachedFiles?.length >= MAX_FILES
                      ? `Maximale Anzahl von ${MAX_FILES} Dateien erreicht`
                      : 'Dateien hier ablegen oder klicken zum Auswählen'
                    }
                  </span>
                  <span className="text-xs text-slate-500">
                    PDF, DOC, DOCX, TXT, JPG, PNG, WEBP (max. 10MB pro Datei)
                  </span>
                </label>
              </div>

              {/* Display file errors */}
              {fileErrors?.length > 0 && (
                <div className="mt-2 p-3 bg-red-50 border border-red-200 rounded-md">
                  {fileErrors?.map((error, index) => (
                    <p key={index} className="text-sm text-red-700">{error}</p>
                  ))}
                </div>
              )}

              {/* Display attached files */}
              {attachedFiles?.length > 0 && (
                <div className="mt-4 space-y-2">
                  <p className="text-sm font-medium text-text-primary">
                    Angehängte Dateien ({attachedFiles?.length}/{MAX_FILES}):
                  </p>
                  {attachedFiles?.map((file, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border"
                    >
                      <div className="flex items-center space-x-3">
                        <svg
                          className="w-5 h-5 text-slate-500"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                          />
                        </svg>
                        <div>
                          <p className="text-sm font-medium text-text-primary">
                            {file?.name}
                          </p>
                          <p className="text-xs text-slate-500">
                            {formatFileSize(file?.size)}
                          </p>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => removeFile(index)}
                        className="text-red-500 hover:text-red-700 p-1 rounded"
                        title="Datei entfernen"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M6 18L18 6M6 6l12 12"
                          />
                        </svg>
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {errors?.submit && (
              <div className="bg-red-50 border border-red-200 rounded-md p-4">
                <div className="text-sm text-red-700">{errors?.submit}</div>
              </div>
            )}

            <div className="flex flex-col sm:flex-row gap-4 pt-6">
              <Button
                type="submit"
                variant="default"
                size="lg"
                loading={isSubmitting}
                iconName="Send"
                iconPosition="left"
                className="bg-trust-builder hover:bg-trust-builder/90 text-white flex-1"
                disabled={uploading}
              >
                {uploading
                  ? 'Dateien werden hochgeladen...'
                  : isSubmitting
                  ? 'Wird gesendet...' :'Schriftliche Beratung anfordern'
                }
              </Button>

              <Button
                type="button"
                variant="outline"
                size="lg"
                iconName="Phone"
                iconPosition="left"
                className="border-conversion-accent text-conversion-accent hover:bg-conversion-accent hover:text-white"
                onClick={() => window.location.href = 'tel:09001000312'}
              >
                Lieber direkt anrufen
              </Button>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
};

export default ContactForm;